import { Backdrop, Button, CircularProgress, Fade, Modal, Stack, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';

import { CustomerListItem } from './filter/companyFilterButton';
import RoleTextField from './inviteUser/roleTextField';
import SelectCompany from './inviteUser/selectCustomer';

import { blueGradientButton } from '../../assets/styles/blueGradientButton.style';
import { axiosInstanceForTenantAPI } from '../../assets/utils/axiosInstance';
import { useToastNotificationContext } from '../../hooks/global/alert/useToastNotification';  
import { CustomError } from '../../interfaces/ErrorType';
import EmailAddressTextField from '../customer/settings/user/emailAddressTextField';
import FullNameTextField from '../customer/settings/user/fullNameTextField';

const style = {
  display: 'flex',
  alignItems: 'flex-start', // Center vertically
  justifyContent: 'center', // Center horizontally
  flexDirection: 'column',
  borderRadius: '6px',
  position: 'fixed',
  top: '20px',
  left: 'calc(100% - 400px)',
  transform: 'translate(-100%, 5%)',
  width: 800,
  bgcolor: 'background.paper',
  boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px', // Updated box-shadow
  p: 3,
};
interface PartnerCustomerInviteProps {
  open: boolean;
  setClose: () => void;
  companyList: CustomerListItem[]
}

const PartnerCustomerInvite = ({ open, setClose, companyList }: PartnerCustomerInviteProps) => {
  const [inviteLoading, setInviteLoading] = useState<boolean>(false);
  const { createToastNotification } = useToastNotificationContext();

  const formik = useFormik({
    initialValues: {
      fullName: '',
      email: '',
      userRole: '5',
      company: '',
      submit: null,
    },
    validationSchema: Yup.object({
      fullName: Yup.string().required('Full Name Required.'),
      email: Yup.string()
        .max(255)
        .required('Email Required.')
        .email('Enter valid email.'),
      userRole: Yup.string().max(255).required('User Role Required.'),
      company: Yup.string().required('Company is Required.'),
    }),
    onSubmit: async (values, helpers) => {
      setInviteLoading(true);
      try {
        const inviteUserResponse = await axiosInstanceForTenantAPI(
          values.company,
        ).post('/invite-user', {
          email: values.email,
          user_type: values.userRole,
          full_name: values.fullName,
        });

        const [inviteUser] = await Promise.allSettled([inviteUserResponse]);
        if (inviteUser.status === 'fulfilled') {
          createToastNotification('User invitation successfully sent.', 'success');
          formik.setValues({
            fullName: '',
            email: '',
            userRole: '5',
            company: '',
            submit: null,
          });
          formik.setTouched({});
          formik.setErrors({});
        } else {
          throw new Error(`Error submitting the report: ${inviteUser.reason}`);
        }
      } catch (error) {
        helpers.setStatus({ success: false });
        const customError = error as CustomError;
        const errorMessage =
          customError.response?.data?.email ||
          customError.response?.data?.message ||
          customError.response?.data?.detail ||
          (Array.isArray(customError.response?.data) &&
            customError?.response?.data[0]) ||
          'Something Went wrong';
        helpers.setErrors({ submit: errorMessage });
        helpers.setSubmitting(false);
      } finally {
        setInviteLoading(false);
      }
    },
  });


  return (
    <Modal
      aria-labelledby='transition-modal-title'
      aria-describedby='transition-modal-description'
      open={open}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
      onClose={setClose}
    >
      <Fade in={open}>
        <Stack sx={style}>
          <form noValidate onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
            <Stack 
              direction='row' 
              justifyContent='space-between' 
              alignItems='center' 
              sx={{
                width: '100%',
              }}>
              <Typography
                id='transition-modal-title'
                sx={{
                  fontSize: '18px',
                  fontWeight: 500,
                  lineHeight: '125%',
                  fontFamily: 'Schibsted Grotesk',

                }}
              >
                Customer User
              </Typography>
              <Stack direction='row' gap={2}>
                <Button 
                  variant='text' 
                  sx={{
                    textTransform: 'none',
                    color:'#181818',
                    fontSize:'14px',
                    fontWeight: 600,
                  }}
                  onClick={setClose}>Cancel
                </Button>
                <Button
                  type='submit'
                  sx={{
                    fontStyle: 'normal',
                    fontWeight: 600,
                    ...blueGradientButton,
                    textTransform: 'none',
                  }}>
                  {inviteLoading ? (
                      <>
                        <CircularProgress
                          size={20}
                          style={{ marginRight: '8px', color: 'black' }}
                        />
                      </>
                  ) : (
                    'Invite'
                  )}
                </Button>
              </Stack>
            </Stack>
            <Stack 
              direction='row'
              alignItems='flex-start'
              justifyContent='space-between'
              sx={{ width: '100%', paddingBottom: '.5rem' }}
              spacing={2}
            >
              <FullNameTextField
                error={!!(formik.touched.fullName && formik.errors.fullName)}
                helperText={
                  formik.touched.fullName && formik.errors.fullName
                    ? formik.errors.fullName
                    : ''
                }
                onChange={formik.handleChange}
                userFullName={formik.values.fullName}
                onBlur={formik.handleBlur}
              />

              <EmailAddressTextField
                error={!!(formik.touched.email && formik.errors.email)}
                helperText={
                  formik.touched.email && formik.errors.email
                    ? formik.errors.email
                    : ''
                }
                onChange={formik.handleChange}
                emailValue={formik.values.email}
                onBlur={formik.handleBlur}
              />
            </Stack>
            <Stack 
              direction='row'
              alignItems='flex-start'
              justifyContent='space-between'
              sx={{ width: '100%', paddingBottom: '.5rem' }}
              spacing={2}
            >
              <SelectCompany
                error={!!(formik.touched.company && formik.errors.company)}
                helperText={
                  formik.touched.company && formik.errors.company
                    ? formik.errors.company
                    : ''
                }
                onChange={formik.handleChange}
                companyIdValue={formik.values.company}
                onBlur={formik.handleBlur}
                companyList={companyList}
              />
              <RoleTextField
                error={!!(formik.touched.userRole && formik.errors.userRole)}
                helperText={
                  formik.touched.userRole && formik.errors.userRole
                    ? formik.errors.userRole
                    : ''
                }
                onChange={formik.handleChange}
                userRole={formik.values.userRole}
                onBlur={formik.handleBlur}
              />
            </Stack>
            <Stack
              direction='row'
              alignItems='flex-start'
              justifyContent='space-between'
              sx={{ width: '100%' }}
              spacing={2}
            >
              {formik.errors.submit && (
                <Typography
                  color='error'
                  variant='body2'
                  sx={{ padding: '10px 0 10px 0' }}
                >
                  {formik.errors.submit}
                </Typography>
              )}
            </Stack>
          </form>
        </Stack>
      </Fade>
    </Modal>
  );
};

export default PartnerCustomerInvite;